import React from 'react';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  Code as CodeIcon
} from '@mui/icons-material';

/**
 * PullRequestStats component for displaying PR statistics
 * @param {Object} props - Component props
 * @param {Object} props.stats - Statistics data
 * @param {boolean} props.loading - Loading state
 * @returns {JSX.Element} PullRequestStats component
 */
const PullRequestStats = ({ stats, loading }) => {
  if (loading) {
    return (
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
          Loading statistics...
        </Typography>
      </Box>
    );
  }

  if (!stats) {
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" color="textSecondary" align="center">
          No statistics available
        </Typography>
      </Box>
    );
  }

  const {
    totalPRs = 0,
    openPRs = 0,
    closedPRs = 0,
    mergedPRs = 0
  } = stats;

  const mergeRate = totalPRs > 0 ? Math.round((mergedPRs / totalPRs) * 100) : 0;

  const StatCard = ({ title, value, icon, color = 'primary', subtitle }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box display="flex" alignItems="center" mb={1}>
          {icon}
          <Typography variant="h6" component="div" sx={{ ml: 1, color: `${color}.main` }}>
            {title}
          </Typography>
        </Box>
        <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color: `${color}.main` }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="textSecondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Pull Request Statistics
      </Typography>
      
      <Grid container spacing={3}>
        {/* Overview Stats */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total PRs"
            value={totalPRs}
            icon={<CodeIcon color="primary" />}
            color="primary"
            subtitle={`${openPRs} open, ${closedPRs} closed`}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Merged PRs"
            value={mergedPRs}
            icon={<CheckCircleIcon color="success" />}
            color="success"
            subtitle={`${mergeRate}% merge rate`}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Open PRs"
            value={openPRs}
            icon={<ScheduleIcon color="warning" />}
            color="warning"
            subtitle="Currently open"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default PullRequestStats;
